import { useState } from 'react';
import { useLive } from '@/lib/use-live';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { executedFromPieces } from '@obraplan/core/domain/takeoff/piece-execution';
import type { FrontPiecesView } from '@obraplan/core/application/contracts/takeoff';

/**
 * As peças do dia (Fase 27, etapa 6 — D21 a D23; Fase 28, etapa 2 — RN-411 a RN-413).
 *
 * A frente × serviço que tem peça — a estaca, o bloco, o arranque — mostra aqui as peças dela
 * com uma caixa de marcar: o encarregado diz "B93 concretado" em vez de lembrar quantos metros
 * cúbicos isso deu. A soma das medidas marcadas **preenche** o campo de quantidade do dia
 * (RN-382), e o campo continua editável: quem rendeu diferente sobrescreve.
 *
 * A peça vem da planilha importada ou do desenho, e o **nome** junta as duas (ADR-178): a
 * importada se executa sem estar desenhada, com o número da planilha (RN-411, RN-412). Por isso
 * a chave aqui é o nome, e é ele que vai para a gravação.
 *
 * A peça executada em outro dia, neste mesmo serviço, aparece desabilitada com a data
 * (RN-381): uma peça não se executa duas vezes no mesmo serviço, e refazer é desmarcar no dia
 * em que foi marcada e marcar de novo neste.
 *
 * Componente de leitura: quem grava é o pai, **ao salvar o dia** — a marca e a quantidade
 * saem juntas ou nenhuma sai. A tela não conhece o container: recebe `load`, o que a deixa
 * testável e a mantém fora dos lugares onde a frente não tem peça (aí ela não desenha nada).
 *
 * É **controlada**: `selected` ausente quer dizer "como está gravado hoje" (as peças já
 * marcadas nesta data). O pai guarda a seleção para que ela sobreviva a trocar de passo no
 * diálogo, e só a grava se a pessoa mexeu.
 */
export type CarregarPecas = (input: {
  activityId: string;
  serviceId: string;
  date: string;
}) => Promise<FrontPiecesView | undefined>;

export interface MudancaDePecas {
  /** Os nomes das peças marcadas, na ordem da lista. */
  readonly pieceNames: readonly string[];
  /** A soma das medidas marcadas; ausente quando nenhuma marcada tem medida. */
  readonly total: number | undefined;
  /**
   * As peças que estavam marcadas neste dia quando a pessoa abriu a lista (Fase 43, RN-615). A
   * fila do aparelho manda a diferença entre esta e `pieceNames`, aplicada à lista que o
   * servidor tiver na hora de subir: a lista velha não desfaz a marca de outro aparelho.
   */
  readonly previous: readonly string[];
}

export interface PecasDoDiaProps {
  activityId: string;
  serviceId: string;
  /** AAAA-MM-DD do dia que se lança. */
  date: string;
  load: CarregarPecas;
  /** A unidade do serviço: é nela que a medida de cada peça se escreve. */
  unit: { readonly name: string; readonly decimals: number };
  /** Os nomes das peças marcadas. `undefined` = as que o servidor já tem nesta data. */
  selected: readonly string[] | undefined;
  onChange: (change: MudancaDePecas) => void;
  disabled?: boolean;
}

/** A partir de quantas peças a lista ganha a busca pelo nome. */
const BUSCA_A_PARTIR_DE = 12;

/** `2026-10-03` → `03/10`. */
function diaEMes(data: string): string {
  const [, mes, dia] = data.split('-');
  return mes === undefined || dia === undefined ? data : `${dia}/${mes}`;
}

/** "b 93" acha "B93": a busca ignora caixa, acento e espaço. */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '').toLowerCase();
}

export function PecasDoDia({
  activityId,
  serviceId,
  date,
  load,
  unit,
  selected,
  onChange,
  disabled = false,
}: PecasDoDiaProps) {
  const view = useLive(() => load({ activityId, serviceId, date }), [activityId, serviceId, date]);
  const [busca, setBusca] = useState('');

  // Frente sem peça não desenha nada — nem enquanto carrega, para não piscar uma seção que
  // quase sempre fica vazia.
  if (view === undefined) return null;

  const formatar = (valor: number): string =>
    `${valor.toLocaleString('pt-BR', {
      minimumFractionDigits: unit.decimals,
      maximumFractionDigits: unit.decimals,
    })} ${unit.name}`;

  if (view.pieces.length === 0) {
    // O serviço que mede um tipo do desenho avisa por que não há lista; o resto fica quieto.
    return view.pieceKind === undefined ? null : (
      <p className="text-xs text-(--color-content-muted)">
        Nem a planilha nem o desenho deste pavimento têm peças para este serviço — a quantidade se
        digita.
      </p>
    );
  }

  const pecas = view.pieces;
  const livres = new Set(
    pecas
      .filter((peca) => peca.executedOn === undefined || peca.executedHere)
      .map((peca) => peca.name),
  );
  const marcadas = new Set(
    (selected ?? pecas.filter((peca) => peca.executedHere).map((peca) => peca.name)).filter(
      (nome) => livres.has(nome),
    ),
  );

  const comBusca = pecas.length > BUSCA_A_PARTIR_DE;
  const termo = normalizar(busca);
  const visiveis =
    comBusca && termo !== ''
      ? pecas.filter((peca) => normalizar(peca.name).includes(termo))
      : pecas;

  const mudar = (proximas: ReadonlySet<string>): void => {
    // A ordem é a da lista, para o envio ser o mesmo qualquer que seja a ordem dos cliques.
    const escolhidas = pecas.filter((peca) => proximas.has(peca.name));
    onChange({
      pieceNames: escolhidas.map((peca) => peca.name),
      total: executedFromPieces(escolhidas.map((peca) => peca.measure)),
      previous: pecas.filter((peca) => peca.executedHere).map((peca) => peca.name),
    });
  };

  const alternar = (nome: string): void => {
    const proximas = new Set(marcadas);
    if (proximas.has(nome)) proximas.delete(nome);
    else proximas.add(nome);
    mudar(proximas);
  };

  // "Marcar as que faltam" vale para o que está à vista: com a busca "B9", marca as B9x, e não
  // a fundação inteira.
  const faltam = visiveis.filter((peca) => livres.has(peca.name) && !marcadas.has(peca.name));
  const soma = executedFromPieces(
    pecas.filter((peca) => marcadas.has(peca.name)).map((peca) => peca.measure),
  );
  const filtrando = comBusca && termo !== '';

  return (
    <fieldset className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3">
      <legend className="px-1 text-sm font-medium">Peças executadas hoje</legend>
      {comBusca && (
        <Input
          type="search"
          aria-label="Buscar peça pelo nome"
          placeholder="Buscar peça — ex.: B93"
          value={busca}
          disabled={disabled}
          onChange={(event) => setBusca(event.target.value)}
        />
      )}
      {visiveis.length === 0 ? (
        <p className="text-xs text-(--color-content-muted)">Nenhuma peça com “{busca.trim()}”.</p>
      ) : (
        <ul
          className={`grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 ${comBusca ? 'max-h-72 overflow-y-auto' : ''}`}
        >
          {visiveis.map((peca) => {
            const emOutroDia = peca.executedOn !== undefined && !peca.executedHere;
            return (
              <li key={peca.name}>
                <label className="flex min-h-9 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={marcadas.has(peca.name)}
                    disabled={disabled || emOutroDia}
                    onChange={() => alternar(peca.name)}
                  />
                  <span className={emOutroDia ? 'text-(--color-content-muted)' : undefined}>
                    {peca.name}
                    <span className="text-(--color-content-muted)">
                      {' · '}
                      {peca.measure === undefined ? 'sem medida' : formatar(peca.measure)}
                    </span>
                    {emOutroDia && peca.executedOn !== undefined && (
                      <span className="text-xs"> — feita em {diaEMes(peca.executedOn)}</span>
                    )}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="min-h-11 md:min-h-9"
          disabled={disabled || faltam.length === 0}
          onClick={() => mudar(new Set([...marcadas, ...faltam.map((peca) => peca.name)]))}
        >
          {filtrando ? 'Marcar as da busca que faltam' : 'Marcar todas as que faltam'}
        </Button>
        <p className="text-xs text-(--color-content-muted)" aria-live="polite">
          {marcadas.size === 0
            ? 'Nenhuma peça marcada.'
            : `${String(marcadas.size)} de ${String(pecas.length)} marcada${marcadas.size === 1 ? '' : 's'}${
                soma === undefined ? '' : ` · ${formatar(soma)}`
              }. A soma preenche a quantidade do dia, que continua editável.`}
        </p>
      </div>
    </fieldset>
  );
}
